/**
 * VolumePricingEditor — Modal editor for quantity-based price tiers.
 *
 * Bottom sheet on mobile, centered dialog on desktop (max-w-[560px]).
 * Multi-row tier editor with auto-gap-fill, live preview, client-side
 * validation, and atomic save.
 *
 * Part of Plan 45 (Volume Pricing), Epic 02.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/format/currency";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  useSaveVolumeTiers,
  type VolumeTier,
} from "@/features/inventory/api/volumeTierHooks";

// ── Types ────────────────────────────────────────────────────

interface TierRow {
  fromQty: string;
  toQty: string; // empty string = unlimited (last tier)
  price: string;
}

interface VolumePricingEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemCode: string;
  basePrice: number;
  currency: string;
  existingTiers: VolumeTier[];
}

// ── Helpers ──────────────────────────────────────────────────

function tiersToRows(tiers: VolumeTier[]): TierRow[] {
  if (tiers.length === 0) {
    return [{ fromQty: "10", toQty: "", price: "" }];
  }
  return tiers.map((t) => ({
    fromQty: String(t.min_qty),
    toQty: t.max_qty != null ? String(t.max_qty) : "",
    price: String(t.rate),
  }));
}

function discountPct(rate: number, basePrice: number): number | null {
  if (!basePrice || basePrice <= 0) return null;
  const pct = Math.round((1 - rate / basePrice) * 100);
  return pct > 0 ? pct : null;
}

// ── Validation ───────────────────────────────────────────────

interface TierError {
  fromQty?: string;
  toQty?: string;
  price?: string;
}

function validateRows(rows: TierRow[]): { errors: TierError[]; isValid: boolean } {
  const errors: TierError[] = rows.map(() => ({}));
  let isValid = true;

  let prevMax = 0;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const from = parseInt(row.fromQty);
    const to = row.toQty ? parseInt(row.toQty) : 0;
    const price = parseFloat(row.price);

    // From validation
    if (isNaN(from) || from < 2) {
      errors[i].fromQty = "Min 2";
      isValid = false;
    } else if (i > 0 && prevMax > 0 && from !== prevMax + 1) {
      errors[i].fromQty = `Must be ${prevMax + 1}`;
      isValid = false;
    }

    // To validation
    if (row.toQty !== "") {
      if (isNaN(to) || to < from) {
        errors[i].toQty = `Must be ≥ ${from || "From"}`;
        isValid = false;
      }
      // Only last tier can be unlimited
    } else if (i < rows.length - 1) {
      errors[i].toQty = "Required (not last)";
      isValid = false;
    }

    // Price validation
    if (isNaN(price) || price <= 0) {
      errors[i].price = "Must be > 0";
      isValid = false;
    }

    prevMax = row.toQty ? to : 0;
  }

  return { errors, isValid };
}

// ── Component ────────────────────────────────────────────────

export function VolumePricingEditor({
  open,
  onOpenChange,
  itemCode,
  basePrice,
  currency,
  existingTiers,
}: VolumePricingEditorProps) {
  const [rows, setRows] = useState<TierRow[]>(() => tiersToRows(existingTiers));
  const saveMutation = useSaveVolumeTiers(itemCode);

  // Reset rows when modal opens
  useEffect(() => {
    if (open) {
      setRows(tiersToRows(existingTiers));
      saveMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { errors, isValid } = useMemo(() => validateRows(rows), [rows]);
  const hasAnyContent = rows.some((r) => r.price || r.fromQty !== "10");

  // ── Row handlers ─────────────────────────────────────────

  const updateRow = useCallback((index: number, field: keyof TierRow, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };

      // Auto-gap-fill: when "To" changes, update next row's "From"
      if (field === "toQty" && index < next.length - 1 && value !== "") {
        const toVal = parseInt(value);
        if (!isNaN(toVal)) {
          next[index + 1] = { ...next[index + 1], fromQty: String(toVal + 1) };
        }
      }

      return next;
    });
  }, []);

  const addRow = useCallback(() => {
    if (rows.length >= 10) return;
    setRows((prev) => {
      const lastRow = prev[prev.length - 1];
      const lastTo = lastRow?.toQty ? parseInt(lastRow.toQty) : 0;
      const newFrom = lastTo > 0 ? String(lastTo + 1) : "";
      return [...prev, { fromQty: newFrom, toQty: "", price: "" }];
    });
  }, [rows.length]);

  const deleteRow = useCallback((index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ── Save handler ─────────────────────────────────────────

  const handleSave = useCallback(() => {
    // Convert rows to tier params
    const tiers = rows.map((r) => ({
      min_qty: parseInt(r.fromQty),
      max_qty: r.toQty ? parseInt(r.toQty) : null,
      rate: parseFloat(r.price),
    }));

    saveMutation.mutate(
      { item_code: itemCode, tiers },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          if (result.length === 0) {
            toast.success("Volume pricing removed");
          } else {
            toast.success(`Volume pricing saved (${result.length} tier${result.length !== 1 ? "s" : ""})`);
          }
        },
      }
    );
  }, [rows, itemCode, saveMutation, onOpenChange]);

  const handleRemoveAll = useCallback(() => {
    saveMutation.mutate(
      { item_code: itemCode, tiers: [] },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast.success("Volume pricing removed");
        },
      }
    );
  }, [itemCode, saveMutation, onOpenChange]);

  // ── Preview data ─────────────────────────────────────────

  const previewRows = useMemo(() => {
    const result: { label: string; price: string; savings: number | null }[] = [];

    const validRows = rows.filter((r) => {
      const from = parseInt(r.fromQty);
      const price = parseFloat(r.price);
      return !isNaN(from) && !isNaN(price) && price > 0;
    });

    if (validRows.length === 0) return result;

    // Base price row
    const firstFrom = parseInt(validRows[0].fromQty);
    if (firstFrom > 1) {
      result.push({
        label: firstFrom === 2 ? "1" : `1 – ${firstFrom - 1}`,
        price: formatPrice(basePrice, currency),
        savings: null,
      });
    }

    // Tier rows
    for (const row of validRows) {
      const from = parseInt(row.fromQty);
      const to = row.toQty ? parseInt(row.toQty) : null;
      const price = parseFloat(row.price);
      const label = to != null ? `${from} – ${to}` : `${from}+`;

      result.push({
        label,
        price: formatPrice(price, currency),
        savings: discountPct(price, basePrice),
      });
    }

    return result;
  }, [rows, basePrice, currency]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "sm:max-w-[560px] sm:rounded-2xl p-0 gap-0 overflow-hidden",
          "max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:top-auto",
          "max-sm:translate-x-0 max-sm:translate-y-0",
          "max-sm:rounded-t-3xl max-sm:rounded-b-none",
          "max-sm:max-h-[90vh] max-sm:overflow-y-auto",
          "max-sm:data-[state=open]:slide-in-from-bottom",
          "max-sm:data-[state=closed]:slide-out-to-bottom"
        )}
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="text-lg font-semibold">
            Volume Pricing
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Base price: {formatPrice(basePrice, currency)}
          </p>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-5">
          {/* Tier rows */}
          <div className="space-y-3">
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 max-sm:hidden">
              <span className="text-xs text-muted-foreground">From qty</span>
              <span className="text-xs text-muted-foreground">To qty</span>
              <span className="text-xs text-muted-foreground">Price per unit</span>
              <span className="w-9" />
            </div>

            {rows.map((row, i) => (
              <div key={i} className="space-y-1">
                {/* Desktop: single row */}
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-start max-sm:grid-cols-[1fr_1fr_auto] max-sm:gap-x-2 max-sm:gap-y-1.5">
                  {/* From */}
                  <div>
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="From"
                      value={row.fromQty}
                      onChange={(e) => updateRow(i, "fromQty", e.target.value)}
                      className={cn(
                        "min-h-[44px] text-base tabular-nums",
                        errors[i]?.fromQty && "border-red-400"
                      )}
                    />
                    {errors[i]?.fromQty && (
                      <p className="text-[11px] text-red-500 mt-0.5">{errors[i].fromQty}</p>
                    )}
                  </div>

                  {/* To */}
                  <div>
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder={i === rows.length - 1 ? "∞" : "To"}
                      value={row.toQty}
                      onChange={(e) => updateRow(i, "toQty", e.target.value)}
                      className={cn(
                        "min-h-[44px] text-base tabular-nums",
                        errors[i]?.toQty && "border-red-400"
                      )}
                    />
                    {errors[i]?.toQty && (
                      <p className="text-[11px] text-red-500 mt-0.5">{errors[i].toQty}</p>
                    )}
                  </div>

                  {/* Delete button (mobile: in row 1) */}
                  <div className="sm:hidden flex items-center justify-end">
                    {rows.length > 0 && (
                      <button
                        type="button"
                        onClick={() => deleteRow(i)}
                        className="p-2 rounded-sm text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label={`Delete tier ${i + 1}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Price (mobile: full row 2) */}
                  <div className="max-sm:col-span-2">
                    <div className="relative">
                      <Input
                        type="number"
                        inputMode="decimal"
                        placeholder="Price"
                        value={row.price}
                        onChange={(e) => updateRow(i, "price", e.target.value)}
                        className={cn(
                          "min-h-[44px] text-base tabular-nums pr-16",
                          errors[i]?.price && "border-red-400"
                        )}
                      />
                      {/* Discount badge inside input */}
                      {row.price && parseFloat(row.price) > 0 && (() => {
                        const pct = discountPct(parseFloat(row.price), basePrice);
                        return pct ? (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-emerald-600 tabular-nums">
                            −{pct}%
                          </span>
                        ) : null;
                      })()}
                    </div>
                    {errors[i]?.price && (
                      <p className="text-[11px] text-red-500 mt-0.5">{errors[i].price}</p>
                    )}
                  </div>

                  {/* Delete button (desktop) */}
                  <div className="max-sm:hidden flex items-center">
                    {rows.length > 0 && (
                      <button
                        type="button"
                        onClick={() => deleteRow(i)}
                        className="p-2 rounded-sm text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                        aria-label={`Delete tier ${i + 1}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Add tier button */}
            {rows.length < 10 && (
              <Button
                type="button"
                variant="ghost"
                onClick={addRow}
                className="w-full min-h-[44px] text-sm text-muted-foreground hover:text-foreground"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Add tier
              </Button>
            )}
            {rows.length >= 10 && (
              <p className="text-xs text-muted-foreground text-center py-2">
                Maximum 10 tiers reached
              </p>
            )}
          </div>

          {/* Live preview */}
          {previewRows.length > 0 && (
            <div className="border-t border-border/20 pt-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Your customers will see:
              </p>
              <div className="space-y-1.5">
                {previewRows.map((row, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="tabular-nums text-muted-foreground">{row.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="tabular-nums font-medium">{row.price}</span>
                      {row.savings != null && (
                        <span className="text-xs text-emerald-600 tabular-nums">
                          Save {row.savings}%
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error alert */}
          {saveMutation.isError && (
            <div className="rounded-sm bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {saveMutation.error?.message || "Failed to save volume pricing. Please try again."}
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-2">
            {rows.length > 0 ? (
              <Button
                onClick={handleSave}
                disabled={!isValid || saveMutation.isPending}
                className="w-full min-h-[44px] rounded-full font-semibold"
                style={{ backgroundColor: isValid ? "#007AFF" : undefined }}
              >
                {saveMutation.isPending ? "Saving…" : "Save"}
              </Button>
            ) : (
              <Button
                onClick={handleRemoveAll}
                disabled={saveMutation.isPending}
                variant="outline"
                className="w-full min-h-[44px] rounded-full font-semibold text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                {saveMutation.isPending ? "Removing…" : "Remove Volume Pricing"}
              </Button>
            )}

            {/* Remove all shortcut when tiers exist */}
            {rows.length > 0 && hasAnyContent && existingTiers.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setRows([])}
                className="w-full min-h-[44px] text-sm text-muted-foreground hover:text-red-500"
              >
                Remove all tiers
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
